// Copy for the Tell Us Once divergence frames. Illustrative language — the
// point is the tone and the type of conversation, not final wording. Vez's
// rough copy from the divergence doc is kept verbatim where he wrote it.

// A concrete care recipient, because low-fi frames read better with a real
// name in them than with [name].
export const PERSON = {
  name: 'Margaret',
  relation: 'Mum',
};

// Declared here rather than imported from Chat so the copy stays the lower
// layer: the transcript component consumes content, never the other way round.
export type Turn = {
  from: 'us' | 'user';
  text: string;
};

// Identity helper that pins a transcript to Turn[]. Without it `from` widens
// to string on every literal and no transcript can be passed to Chat.
const asTurns = (turns: Turn[]): Turn[] => turns;

// A row on the review screen. `state` is absent for the settled majority —
// answered and not currently being touched — so only the exceptions carry one.
export type ReviewRow = {
  question: string;
  value: string;
  state?: 'answering' | 'open';
};

// A — the way in.
export const WAY_IN = {
  // A2 — the first thing asked after the account exists. Deliberately about
  // the person being cared for, not the carer: it sets up who everything
  // after this is about.
  a2: {
    title: 'Who are you caring for?',
    body: 'We only need a few things to work out what they might be entitled to.',
    nameLabel: 'Their first name',
    nameValue: 'Margaret',
    relationLabel: 'Your relationship to them',
    relationValue: 'Mum',
    cta: 'Continue',
  },
  a3: {
    progress: 'Question 3 of about 8',
    question: 'Does Margaret need help washing or bathing?',
    options: [
      'Yes, most days',
      'Yes, now and then',
      'No, she manages on her own',
    ],
    selected: 'Yes, most days',
    // The branch that opens as a result — the whole point of the frame.
    followUp: 'What kind of help does she need?',
    followUpOptions: [
      'Someone nearby in case she slips',
      'A hand getting in and out',
      'Washing her himself or herself',
    ],
    cta: 'Continue',
    branchNote: 'Opened by your answer',
  },
  a4: {
    turns: asTurns([
      { from: 'us', text: 'Tell me a bit about the person you’re caring for.' },
      {
        from: 'user',
        text: 'My mum Margaret. She’s 81 and lives on her own since dad died.',
      },
      {
        from: 'us',
        text: 'And how are they managing day to day — washing, dressing, getting around?',
      },
    ]),
    // A2 is voice-first: the orb is the way in, typing is the fallback.
    typePrompt: 'Or type instead',
  },
  // A5 sits after the fork, so it is asked the way the conversation asks
  // everything else: said out loud, answered by typing, with a way to park it.
  // Drawing it as a form screen would have quietly contradicted A4.
  a5: {
    turns: asTurns([
      {
        from: 'us',
        text: 'Caring is rarely a solo job. Is anyone else helping with Margaret?',
      },
      { from: 'user', text: 'Yes, my sister helps me.' },
      // The reply picks up "sister" and switches to her — a small proof that
      // it heard the answer rather than moving to the next scripted line.
      {
        from: 'us',
        text: 'Pop her email in and I’ll invite her. She’ll see the same claims you do.',
      },
    ]),
    options: [{ label: 'I’ll do this later', icon: 'clock' }],
    typePrompt: 'Their email…',
  },
  a6: {
    lead: 'Based on what you’ve told us, Margaret could be entitled to:',
    // 'likely' is a calculator estimate; 'confirmed' has been checked. The
    // frame exists to test showing the difference without over-promising.
    benefits: [
      {
        name: 'Attendance Allowance',
        amount: '£441 a month',
        certainty: 'likely' as const,
        note: 'Based on the help she needs washing and at night',
      },
      {
        name: 'Council Tax reduction',
        amount: 'Up to £150 a month',
        certainty: 'likely' as const,
        note: 'Depends on her savings, which we haven’t asked about yet',
      },
      {
        name: 'Carer’s Allowance',
        amount: '£327 a month',
        certainty: 'check' as const,
        note: 'Only if you care for her 35+ hours a week',
      },
    ],
    cta: 'Start with Attendance Allowance',
    secondary: 'See how we worked this out',
  },
  // A7 — where "I'll do this later" ends up: the real actions screen, seeded
  // with the parked invite instead of the stock demo actions. `kind: 'user'`
  // because you set it yourself by deferring, not a CAM — which is what makes
  // it read as your own to-do rather than something assigned to you.
  a7: {
    action: {
      id: 101,
      title: 'Add your sister’s email',
      schedule: 'You said you’d do this later',
      who: 'V',
      regarding: 'Regarding Margaret’s care circle',
      createdBy: 'you',
      assignedTo: 'Assigned to you',
      due: 'No date set',
      note: undefined,
      files: [],
      kind: 'user' as const,
      done: false,
    },
  },
};

// The high-level flow — the whole journey end to end, at low fidelity. Where
// the lettered sections interrogate one moment each, this is the story they
// sit inside, so nothing here goes deeper than it needs to.
export const FLOW = {
  // F2 — home as the starting point. One task, because at this stage there is
  // exactly one thing worth doing and a list of one is not a list.
  home: {
    greeting: 'Afternoon, Vez',
    lead: 'First things first',
    task: {
      title: 'Tell us about your care situation',
      detail: 'About 5 minutes. We’ll work out what you might be owed.',
      chip: 'Start here',
    },
    typePrompt: 'Or just start talking',
  },

  // F3 — who is asking. The branch matters: a carer has two people to name,
  // someone claiming for themselves has one, and asking a self-claimant for
  // "their" name as well would be the exact clumsiness this whole idea is
  // meant to remove.
  whoAreYou: {
    turns: asTurns([
      {
        from: 'us',
        text: 'First up — are you caring for someone, or looking into this for yourself?',
      },
      { from: 'user', text: 'I look after my mum.' },
      {
        from: 'us',
        text: 'Got it. Names have to be spot on for the claim, so type these rather than saying them.',
      },
    ]),
    // Typed, not spoken. A misheard name on a government claim is a rejection,
    // and this is the one place in the flow where exactness beats ease — so the
    // conversation hands over to fields and says why.
    fields: [
      { label: 'Your name', value: 'Vez' },
      { label: 'Their name', value: 'Margaret' },
    ],
    branchNote: 'If they were claiming for themselves, we would only ask once.',
    cta: 'That’s right',
  },

  // F5 — the moment a spoken answer is not enough. Shown mid-type with the
  // keyboard up, because the question is how jarring the handoff from talking
  // to typing feels.
  typing: {
    turns: asTurns([
      {
        from: 'us',
        text: 'I’ll need Margaret’s National Insurance number for this. It’s on her payslips or pension letters.',
      },
    ]),
    typed: 'QQ 12 34 56 ',
    placeholder: 'NI number',
  },

  // F7 — starting AA. Compressed on purpose: section C has the whole thing,
  // this is here to show the claim begins as more of the same conversation
  // rather than as a form appearing.
  aaStart: {
    turns: asTurns([
      {
        from: 'us',
        text: 'Right, Attendance Allowance. I already know she needs help washing, so I won’t ask again.',
      },
      { from: 'us', text: 'How are her nights?' },
      { from: 'user', text: 'She’s up three or four times most nights.' },
    ]),
    typePrompt: 'Or type instead',
  },

  // F8 — skipping. Same defer pattern as C2, kept here so the flow shows what
  // happens when someone does not have an answer to hand.
  skip: {
    turns: asTurns([
      {
        from: 'us',
        text: 'Next I need her GP’s address. Do you have it handy?',
      },
    ]),
    options: [
      { label: 'Skip for now', icon: 'clock' },
      { label: 'Snap a photo of a letter', icon: 'images' },
    ],
    typePrompt: 'Or type it in',
  },

  // F10 — review, following the pattern the financial assessment already uses:
  // grouped rows in one card per section, the question above its answer, tap a
  // row to change it. Unanswered rows read "Not answered" rather than being
  // hidden, so what is missing is visible in the same list as what is done.
  //
  // The one departure: the skipped items are answered in place rather than in
  // a drawer, because they arrived here by being deferred and sending someone
  // somewhere else to finish them would repeat the thing they avoided.
  review: {
    title: 'Almost there',
    lead: 'This is everything we’ll send. Two things still need you.',
    groups: [
      {
        title: 'Still needed',
        rows: <ReviewRow[]>[
          {
            question: 'Margaret’s GP address',
            value: 'The Grove Surgery, Hillside Rd',
            state: 'answering',
          },
          { question: 'Her bank details', value: '', state: 'open' },
        ],
      },
      {
        title: 'About Margaret',
        rows: <ReviewRow[]>[
          { question: 'Her date of birth', value: '4 March 1945' },
          { question: 'Her address', value: '12 Rowan Close, Leeds' },
          { question: 'National Insurance number', value: 'QQ 12 34 56 C' },
        ],
      },
      {
        title: 'Help at night',
        rows: <ReviewRow[]>[
          {
            question: 'Does she need help at night?',
            value: 'Yes, three or four times',
          },
          {
            question: 'What does she need help with?',
            value: 'Getting to the toilet, and settling again',
          },
        ],
      },
    ],
    cta: 'Submit the claim',
  },

  // F11 — the end. A call is the offer, not a requirement: the claim is
  // already in, and the CAM is there for what happens next.
  submitted: {
    headline: 'That’s gone in.',
    body: 'We’ll let you know as soon as we hear anything. It usually takes about three weeks.',
    callTitle: 'Book a call with your care expert',
    callDetail:
      'They can check what else Margaret might be owed and help with the next claim.',
    cta: 'Book a call',
    secondary: 'Not right now',
  },
};

// The Attendance Allowance form against the conversation that fills it —
// read conversation-first, because that is the honest direction.
//
// A form is linear because paper is linear. A conversation is not. One thing
// someone says lands in several places at once, and one page of the form is
// usually filled from moments minutes apart. An earlier version of this paired
// each page with "its" conversation, which quietly asserted a tidiness that
// does not exist — and the tangle is the strongest argument for talking rather
// than filling it in yourself, so it should be the headline rather than
// something the layout hides.
//
// IMPORTANT: the page numbers are best-effort. Nobody has walked the 30-page
// PDF to confirm which page carries which part, so treat every landing as a
// proposal until it has been checked.
export const AA_MAPPING = {
  pdf: '/aa/attendance-allowance.pdf',
  pageCount: 30,
  unverifiedNote:
    'Page numbers and highlight positions are both unconfirmed — nobody has walked the PDF. Nudge anything that lands wrong.',
  moments: [
    {
      id: 'document',
      at: 'Minute 1',
      turns: asTurns([
        {
          from: 'us',
          text: 'For this part I need something official with Margaret’s name and address on it.',
        },
        { from: 'user', text: 'What sort of thing?' },
        {
          from: 'us',
          text: 'Any of these will do. Most people use a council tax bill or a pension letter.',
        },
      ]),
      examples: [
        'Council tax bill',
        'Pension letter',
        'Bank statement',
        'Utility bill',
        'Blue badge',
      ],
      options: [
        { label: 'Take a photo', icon: 'images' },
        { label: 'Choose a file', icon: 'file-lines' },
        { label: 'I’ll do this later', icon: 'clock' },
      ],
      lands: [
        {
          page: 5,
          section: 'About the person you are claiming for',
          schema: '1-about-you',
          region: { top: 18, left: 8, width: 84, height: 34 },
          fields: [
            'Full name',
            'Address and postcode',
            'Date of birth, if the document carries it',
          ],
        },
        {
          page: 6,
          section: 'Who lives with them',
          schema: '1-about-you',
          region: { top: 30, left: 8, width: 84, height: 22 },
          fields: ['Single-occupancy discount implies lives alone'],
        },
      ],
    },
    {
      id: 'washing',
      at: 'Minute 2',
      turns: asTurns([
        { from: 'user', text: 'She can’t really wash herself.' },
        {
          from: 'us',
          text: 'When she washes, what does she need — a hand getting in and out, or someone nearby in case she slips?',
        },
        { from: 'user', text: 'Someone there really, she’s unsteady.' },
        { from: 'us', text: 'And is that most days, or now and then?' },
        { from: 'user', text: 'Most days.' },
      ]),
      lands: [
        {
          page: 8,
          section: 'Help with washing and bathing',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 22, left: 8, width: 84, height: 46 },
          fields: [
            'Needs help washing — yes',
            'Type of help — supervision, someone present',
            'How often — most days',
          ],
        },
        {
          page: 16,
          section: 'Supervision during the day',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 14, left: 8, width: 84, height: 40 },
          fields: ['Falls risk — unsteady on her feet'],
        },
      ],
    },
    {
      id: 'dressing',
      at: 'Minute 3',
      turns: asTurns([
        { from: 'us', text: 'And getting dressed — how does she manage?' },
        {
          from: 'user',
          text: 'Buttons and zips are beyond her now. I do her top half.',
        },
        { from: 'us', text: 'Every day, or some days?' },
        { from: 'user', text: 'Every day.' },
      ]),
      lands: [
        {
          page: 9,
          section: 'Help with dressing and undressing',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 20, left: 8, width: 84, height: 44 },
          fields: [
            'Needs help dressing — yes',
            'What help — fastenings, upper body',
            'How often — every day',
          ],
        },
      ],
    },
    {
      id: 'night',
      at: 'Minute 4',
      turns: asTurns([
        { from: 'us', text: 'How are her nights?' },
        { from: 'user', text: 'She’s up three or four times most nights.' },
        { from: 'us', text: 'And what does she need when she’s up?' },
        { from: 'user', text: 'Getting to the toilet, then settling again.' },
      ]),
      lands: [
        {
          page: 12,
          section: 'Help during the night',
          schema: '6-help-with-care-needs-during-the-night',
          region: { top: 16, left: 8, width: 84, height: 48 },
          fields: [
            'Needs help at night — yes',
            'How many times — three or four',
            'What help — toilet, settling again',
          ],
        },
        {
          page: 11,
          section: 'Help with the toilet',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 26, left: 8, width: 84, height: 34 },
          fields: ['Needs help with the toilet — yes'],
        },
        {
          page: 20,
          section: 'How long the help takes',
          schema: '7-help-with-care-needs',
          region: { top: 34, left: 8, width: 84, height: 28 },
          fields: ['Night-time total, derived from times and task'],
        },
      ],
    },
    {
      id: 'gas',
      at: 'Minute 7',
      turns: asTurns([
        {
          from: 'us',
          text: 'Is there a risk to her if she’s left on her own for a while?',
        },
        {
          from: 'user',
          text: 'She leaves the gas on. I check in twice a day.',
        },
      ]),
      lands: [
        {
          page: 16,
          section: 'Supervision during the day',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 14, left: 8, width: 84, height: 40 },
          fields: [
            'Needs supervision — yes',
            'Why — appliances left on',
            'How often — twice daily checks',
          ],
        },
        {
          page: 10,
          section: 'Help with meals',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 24, left: 8, width: 84, height: 30 },
          fields: ['Unsafe preparing a cooked meal — the same hob'],
        },
      ],
    },
    {
      id: 'outdoors',
      at: 'Minute 8',
      turns: asTurns([
        { from: 'us', text: 'How does she get on outside the house?' },
        {
          from: 'user',
          text: 'She won’t go out on her own any more. Someone has to walk with her.',
        },
      ]),
      lands: [
        {
          page: 14,
          section: 'Getting around outdoors',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 20, left: 8, width: 84, height: 40 },
          fields: [
            'Needs help getting around outdoors — yes',
            'What help — accompaniment',
          ],
        },
        {
          page: 16,
          section: 'Supervision during the day',
          schema: '5-help-with-care-needs-during-the-day',
          region: { top: 14, left: 8, width: 84, height: 40 },
          fields: ['Reinforces the falls risk already recorded'],
        },
      ],
    },
    {
      id: 'gp',
      at: 'Minute 9',
      turns: asTurns([
        {
          from: 'us',
          text: 'Next I need her GP’s address. Do you have it handy?',
        },
        { from: 'user', text: 'I don’t have that to hand.' },
        {
          from: 'us',
          text: 'No problem. I’ll put it on your list and we’ll pick it up at review.',
        },
      ]),
      options: [
        { label: 'Snap a photo of a letter', icon: 'images' },
        { label: 'I’ll do this later', icon: 'clock' },
      ],
      lands: [
        {
          page: 18,
          section: 'Your GP and health professionals',
          schema: '3-2-about-gp',
          region: { top: 12, left: 8, width: 84, height: 36 },
          fields: ['Deferred — lands in the actions list, returns at review'],
        },
      ],
    },
    {
      id: 'bank',
      at: 'Minute 11',
      turns: asTurns([
        {
          from: 'us',
          text: 'Last thing — this needs her bank details, exactly as they appear.',
        },
      ]),
      examples: ['Bank statement', 'Paying-in slip', 'Banking app screenshot'],
      options: [
        { label: 'Take a photo', icon: 'images' },
        { label: 'Type them in', icon: 'edit' },
      ],
      lands: [
        {
          page: 24,
          section: 'How you want to be paid',
          schema: '10-how-we-pay-you',
          region: { top: 22, left: 8, width: 84, height: 42 },
          fields: [
            'Account name, sort code, account number',
            'Read from the document, confirmed by you',
          ],
        },
      ],
    },
  ],
};

// An example of the instruction the capture model would run under. Written
// out because "the AI asks like a good CAM would" is the load-bearing claim of
// the whole exploration, and a claim that vague cannot be argued with. Putting
// the actual rules on the page turns it into something engineering can price
// and the care team can correct.
export const AA_PROMPT = {
  title: 'What the model would be told',
  intro:
    'Illustrative, not final. The point is that the behaviour the frames imply can be written down as rules — and that most of those rules come from the form and the care team, not from the model.',
  blocks: [
    {
      heading: 'Your job',
      lines: [
        'You are helping someone claim Attendance Allowance for the person they care for. You are not filling in a form with them. You are having a conversation and quietly filling the form yourself.',
        'You have the full question schema: every question, whether it is required, the DWP guidance under it, and which follow-ups each answer reveals.',
      ],
    },
    {
      heading: 'How to ask',
      lines: [
        'Ask about one part of their day at a time, in the order a person would tell it, not the order the form prints it.',
        'Never read a form question out loud. Translate it. "Do you usually have difficulty or need help during the night?" becomes "How are her nights?"',
        'One question per turn. If you need three things, ask three times.',
        'Use the name they gave you. Never say "the claimant" or "the person you care for".',
      ],
    },
    {
      heading: 'What to do with an answer',
      lines: [
        'Apply every answer to every question it touches, not just the one you asked. "She is up three or four times most nights" answers night-time difficulty, frequency and what help is needed.',
        'If an answer half-fills a required question, ask the one follow-up that completes it. If it fills an optional one, do not chase it.',
        'Never infer a fact the person did not say. You may infer that a question is now answerable and ask it.',
      ],
    },
    {
      heading: 'When to stop',
      lines: [
        'Stop asking about an activity once every required question under it is answered. Say so, and move on.',
        'When every required question in the whole form is answered, stop entirely and say they are through, even if optional questions remain.',
      ],
    },
    {
      heading: 'Exactness',
      lines: [
        'Names, dates of birth, National Insurance numbers, addresses and bank details must be typed or read from a document. Never accept these by voice. Say why: a misheard character is a rejected claim.',
        'Where a document would answer several questions at once, offer that first and name the documents that qualify.',
      ],
    },
    {
      heading: 'When they cannot answer',
      lines: [
        'Accept "I do not know" immediately. Park it, tell them it will come back at review, and carry on.',
        'Never make someone leave the conversation to find something.',
      ],
    },
    {
      heading: 'Showing your work',
      lines: [
        'Every answer you record must carry where it came from: they told you, it was read from a document, or you worked it out from something they said.',
        'Anything you worked out must be confirmed before it is submitted. Anything they said directly does not need re-confirming.',
      ],
    },
  ],
};

// B — the app once claims are known.
export const CLAIMS_APP = {
  b1: {
    greeting: 'Afternoon, Vez',
    lead: 'Margaret’s claims',
    claims: [
      {
        name: 'Attendance Allowance',
        state: 'Ready to start',
        detail: '£441 a month · about 15 minutes',
      },
      {
        name: 'Council Tax reduction',
        state: 'Forming',
        detail: '60% ready from what you’ve told us',
      },
      {
        name: 'Carer’s Allowance',
        state: 'Needs a check',
        detail: 'We need to know your hours first',
      },
    ],
    diaryPrompt: 'Something changed with Margaret?',
    diaryHint: 'Tell us and we’ll keep her claims up to date.',
  },
  b2: {
    claim: 'Attendance Allowance',
    progressLabel:
      '60% ready. We’ve filled what we can from what you’ve told us.',
    sections: [
      {
        label: 'About Margaret',
        state: 'done' as const,
        detail: 'Name, date of birth, address',
      },
      {
        label: 'Washing and bathing',
        state: 'done' as const,
        detail: 'Needs someone with her, most days',
      },
      {
        label: 'Help at night',
        state: 'open' as const,
        detail: 'Not covered yet',
      },
      {
        label: 'Her GP',
        state: 'later' as const,
        detail: 'You said you’d come back to this',
      },
      {
        label: 'Getting around',
        state: 'open' as const,
        detail: 'Not covered yet',
      },
    ],
    cta: 'Carry on',
  },
};

// C — Attendance Allowance, end to end.
export const AA = {
  c1: {
    turns: asTurns([
      { from: 'user', text: 'She can’t really wash herself.' },
      {
        from: 'us',
        text: 'Got it. When she washes or bathes, what does she need — a hand getting in and out, or someone nearby in case she slips?',
      },
      { from: 'user', text: 'Someone there really, she’s unsteady.' },
      { from: 'us', text: 'And is that most days, or now and then?' },
      { from: 'user', text: 'Most days.' },
      {
        from: 'us',
        text: 'So: needs someone with her when she washes, most days, mainly because she’s unsteady. Have I got that right?',
      },
    ]),
    // The real AA form's shape, so the translation is honest about what the
    // form actually asks for. Placeholder until the real screenshot is dropped in.
    formTitle: 'Attendance Allowance claim form',
    formSection: 'Part 2 — Help with washing and bathing',
    formFields: [
      {
        label: 'Do you need help washing or bathing?',
        value: 'Yes',
        filled: true,
      },
      {
        label: 'What help do you need?',
        value: 'Supervision — someone present in case of falls',
        filled: true,
      },
      {
        label: 'How often?',
        value: 'Most days',
        filled: true,
      },
      {
        label: 'How long does this help take each time?',
        value: '',
        filled: false,
      },
    ],
    translationNote: 'One casual sentence, three graded fields',
  },
  c2: {
    turns: asTurns([
      {
        from: 'us',
        text: 'This bit needs their GP’s details. Want to snap a photo of a letter, type it in, or come back to it?',
      },
    ]),
    options: [
      { label: 'Snap a photo', icon: 'images' },
      { label: 'Type it in', icon: 'edit' },
      { label: 'I’ll do this later', icon: 'clock' },
    ],
    tray: '2 things to finish',
  },
  c3: {
    lead: 'Here’s what I’ve understood so far — tap anything that’s not right.',
    // Provenance is the trust piece: every answer says where it came from.
    answers: [
      {
        text: 'Needs someone with her when she washes',
        source: 'you told me' as const,
      },
      {
        text: 'Most days, because she’s unsteady',
        source: 'you told me' as const,
      },
      {
        text: 'Born 4 March 1945',
        source: 'from a document' as const,
      },
      {
        text: 'Likely needs help at night too',
        source: 'worked out from what you said' as const,
      },
    ],
    cta: 'Yes, that’s right',
  },
  c4: {
    lead: 'Almost there. This is everything we’ll send.',
    outstanding: '3 things still need you',
    groups: [
      {
        label: 'About Margaret',
        state: 'confirmed' as const,
        count: '6 answers',
      },
      {
        label: 'Washing and bathing',
        state: 'confirmed' as const,
        count: '4 answers',
      },
      {
        label: 'Help at night',
        state: 'confirmed' as const,
        count: '3 answers',
      },
      { label: 'Her GP', state: 'open' as const, count: 'Still needed' },
      { label: 'Bank details', state: 'open' as const, count: 'Still needed' },
      { label: 'Her signature', state: 'open' as const, count: 'Still needed' },
    ],
    cta: 'Finish the last 3',
    help: 'Not sure about something? Talk to a care expert',
  },
  c5: {
    turns: asTurns([
      { from: 'user', text: 'She gets up three or four times most nights.' },
      {
        from: 'us',
        text: 'That helps — this is exactly the kind of detail AA turns on.',
      },
    ]),
    signal: 'Night-time needs is one of the two things AA is decided on',
  },
  c6: {
    headline: 'You’ve told me enough.',
    body: 'Margaret looks eligible. No need to go further on this bit.',
    detail: 'You answered 11 questions. The form has 47.',
    cta: 'See what happens next',
  },
};

// D — the ambient diary.
export const DIARY = {
  d1: {
    turns: asTurns([
      { from: 'user', text: 'Mum had two falls this week.' },
      {
        from: 'us',
        text: 'Sorry to hear that. I’ve noted it — this matters for her Attendance Allowance. Want to add any detail?',
      },
    ]),
    // The fork the frame exists to expose: one sentence touching three fields.
    routed: ['Mobility', 'Supervision', 'Night-time needs'],
    routedLead: 'This touches',
  },
  d2: {
    lead: 'Your notes',
    entries: [
      {
        date: 'Tuesday',
        raw: 'Mum had two falls this week, second one in the bathroom.',
        madeOf:
          'Unsteady on her feet. Falls risk in the bathroom, which supports the supervision answer on her AA claim.',
        promoted: false,
      },
      {
        date: 'Last Thursday',
        raw: 'GP changed her blood pressure tablets.',
        madeOf: 'Medication change. Not used in any claim yet.',
        promoted: false,
      },
    ],
    confirm: 'Use this in her claim',
    edit: 'That’s not right',
  },
  d3: {
    lead: 'Margaret’s week',
    prompt:
      'Add Margaret’s appointments and we’ll keep the time-based questions answered for you.',
    events: [
      { day: 'Mon', title: 'District nurse', time: '9:00' },
      { day: 'Wed', title: 'Memory clinic', time: '14:30' },
      { day: 'Fri', title: 'Repeat prescription due', time: 'All day' },
    ],
    derived: 'Derived from this: how often she sees a health professional',
    cta: 'Connect a calendar',
  },
};

// E — dropping in and out.
export const DROP_IN = {
  e1: {
    headline: 'Welcome back.',
    body: 'Last time we covered how Margaret manages washing and getting around. Next up: help at night.',
    captured: [
      { label: 'Washing and bathing', state: 'confirmed' as const },
      { label: 'Getting around', state: 'confirmed' as const },
      { label: 'Help at night', state: 'next' as const },
      { label: 'Her GP', state: 'later' as const },
    ],
    cta: 'Carry on',
    secondary: 'Start somewhere else',
  },
  // E2 — the same problem solved without a gate: you land on home, the orb is
  // there to talk to whenever, and the unfinished claim is just a task waiting
  // among the others.
  e2: {
    greeting: 'Afternoon, Vez',
    resume: {
      title: 'Pick up where you left off',
      detail: 'Help at night. About 3 questions to go.',
      chip: 'Attendance Allowance',
    },
    lead: 'Margaret’s claims',
    typePrompt: 'Or tell me what’s changed',
  },
};
